import React from 'react';
import Select, { StylesConfig, components } from 'react-select';
import { Loader } from '@thdinc/hpf-shared';
import { IoChevronDown } from 'react-icons/io5';

interface StyledSelectProps<T = any> {
    options: T[];
    value: T | undefined;
    onChange: (value: any) => void;
    placeholder?: string;
    isCustomComponent?: boolean;
    preferedProduct?: any[];
    isLoading?: boolean;
    height?: number;
    width?: number;
    backgroundColor?: string;
    isSearchable?: boolean;
    isClearable?: boolean;
    valueMaxWidth?: number;
}
const customStyles: StylesConfig<any, false> = {
    control: (provided, state) => ({
        ...provided,
        backgroundColor: '#f7f8fa',
        borderRadius: '9999px',
        minWidth: '150px',
        minHeight: '49px',
        paddingLeft: '14px',
        border: 'none',
        boxShadow: state.menuIsOpen ? '0 0 0 1px #FF5100' : 'none',
        '&:hover': {
            border: 'none',
        },
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#4B5563',
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        color: '#4B5563',
        display: 'block',
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? 'white' : 'white',
        color: state.isSelected ? '#484858' : '#4B5563',
        fontWeight: state.isSelected ? 'normal' : 'normal',
        fontSize: state.isSelected ? '13.5px' : '14px',
        padding: '10px 16px',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: state.isSelected ? '#E2E8F0' : '#F3F4F6',
        },
    }),
    menuList: (provided) => ({
        ...provided,
        padding: 0,
        overflowY: 'auto',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#9ca3b5',
        fontWeight: 400,
        fontSize: 12,
        textTransform: 'none',
    }),
};
const isPreferredOption = (data: any, preferedProduct: any[]) => {
    const productId = data?.product?.id ?? data?.id ?? data?.value;
    if (!productId) return false;
    return preferedProduct.some((p: any) => p.prefer1?.id === productId);
};
const createSingleValue = (preferedProduct: any[] = [], valueMaxWidth?: number) => {
    const SingleValueComponent = (props: any) => {
        const isPreferred = isPreferredOption(props.data, preferedProduct);
        return (
            <components.SingleValue {...props}>
                <div
                    className='flex flex-row items-center gap-1 text-[#000000] font-[400] text-[14px]'
                    style={{ maxWidth: valueMaxWidth ?? 190 }}
                >
                    <span className={`truncate overflow-hidden whitespace-nowrap ${isPreferred ? 'text-primary-red' : ''}`}>
                        {props.data.name}
                    </span>
                    {isPreferred && (
                        <span className='text-primary-red text-[12px] whitespace-nowrap'>{`(Preferred)`}</span>
                    )}
                </div>
            </components.SingleValue>
        );
    };
    SingleValueComponent.displayName = 'CustomSingleValue';
    return SingleValueComponent;
};
const createOption = (preferedProduct: any[] = []) => {
    const OptionComponent = (props: any) => {
        const isPreferred = isPreferredOption(props.data, preferedProduct);
        return (
            <components.Option {...props}>
                <div className='flex items-center justify-between text-xs text-[#000000] font-[400] text-[14px]'>
                    <span className={isPreferred ? 'text-primary-red' : ''}>{props.data.name}</span>
                    {isPreferred && <span className='text-[11px] text-primary-red'>{`(Preferred)`}</span>}
                </div>
            </components.Option>
        );
    };

    OptionComponent.displayName = 'CustomOption';

    return OptionComponent;
};

const DropdownIndicator = (props: any) => {
    const { menuIsOpen } = props.selectProps;

    return (
        <components.DropdownIndicator {...props}>
            <IoChevronDown
                className={`
					w-[14px] h-[14px]
                    mr-2
					text-[#4B5563]
					transition-transform duration-300
					${menuIsOpen ? 'rotate-180' : 'rotate-0'}
				`}
            />
        </components.DropdownIndicator>
    );
};

DropdownIndicator.displayName = 'CustomDropdownIndicator';

const SearchSelect: React.FC<StyledSelectProps> = ({
    options,
    value,
    onChange,
    placeholder,
    isLoading,
    height,
    width,
    backgroundColor = '#f7f8fa',
    isSearchable = true,
    isClearable = false,
    preferedProduct,
    valueMaxWidth,
}) => {
    const isProductLoading = isLoading ? isLoading : false;
    const dynamicStyles: StylesConfig<any, false> = {
        ...customStyles,
        control: (provided, state) => ({
            // eslint-disable-next-line
            ...customStyles.control!(provided, state),
            height: height ? height : provided.height,
            width: width ? width : provided.width,
            minWidth: width ? width : provided.minWidth,
            minHeight: height ? height : provided.minHeight,
            backgroundColor: backgroundColor ? backgroundColor : provided.backgroundColor
        }),
    };

    return (
        <Select
            isSearchable={isSearchable}
            isClearable={isClearable}
            options={options}
            className='capitalize'
            value={value ?? null}
            onChange={onChange}
            placeholder={placeholder}
            styles={dynamicStyles}
            isLoading={isProductLoading}
            getOptionLabel={(option) => option.name ?? option.label}
            getOptionValue={(option) => option.id ?? option.value}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition='fixed'
            /* optional: prevents page jump when open */
            menuShouldBlockScroll={true}
            components={{
                SingleValue: createSingleValue(preferedProduct || [], valueMaxWidth),
                Option: createOption(preferedProduct || []),
                LoadingMessage: () => (
                    <div className='flex items-center justify-center py-2'>
                        <Loader color='bg-red-500' />
                    </div>
                ),
                LoadingIndicator: () => null,
                DropdownIndicator: DropdownIndicator,
            }}
        />
    );
};

export default SearchSelect;
